import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import homeFill from '@iconify/icons-eva/home-fill';
import personFill from '@iconify/icons-eva/person-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// material
import { alpha } from '@mui/material/styles';
import { Button, Box, Divider, MenuItem, Typography } from '@mui/material';
// hooks
import useIsMountedRef from '../../hooks/useIsMountedRef';
// components
import { MIconButton } from '../../components/@material-extend';
import MenuPopover from '../../components/commonComponents/MenuPopover';
import Logo from '../../components/commonComponents/Logo';
import { useSelector } from '../../redux/store';
import { AppState } from '../../redux/rootReducerTypes';
import useLocales from '../../hooks/useLocales';
import { getIdentityClientSettings } from '../../utils/localStorage';
import { AuthMethod } from '../../common/enums';
import { getDisplayName } from '../../utils/formatString';
// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Profile',
    icon: personFill,
    linkTo: '/profile'
  },
  {
    label: 'Change password',
    icon: settings2Fill,
    linkTo: '/change-password'
  }
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const user = useSelector((state: AppState) => state.userState.user);
  const { translate } = useLocales();
  const authMethod = getIdentityClientSettings().authMethod;
  const userInfoState = useSelector((state: AppState) => state.userInformationState);

  let displayName = '';
  let email = '';

  if (user && user.profile) {
    displayName = getDisplayName(user, userInfoState);
    email = `${user.profile.email}`;
  }

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = () => {
    try {
      navigate('/logout');

      if (isMountedRef.current) {
        handleClose();
      }

      localStorage.removeItem('tempData');

    } catch (error) {
      console.error(error);
      enqueueSnackbar(translate('Snackbar.UnableToLogout'), { variant: 'error' });
    }
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <Logo />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {authMethod === AuthMethod.IDM &&
          MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
              sx={{ typography: 'body2', py: 1, px: 2.5 }}
            >
              <Box
                component={Icon}
                icon={option.icon}
                sx={{
                  mr: 2,
                  width: 24,
                  height: 24
                }}
              />

              {option.label}
            </MenuItem>
          ))}
        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            {translate('AccountPopover.Logout')}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
